/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import { Autocomplete as MuiAutocomplete, AutocompleteProps as MuiAutocompleteProps, TextField, TextFieldProps } from '@mui/material';
import React, { useMemo } from 'react';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { IFieldInputProps } from '@ngt/forms-core';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */


type AutocompleteProps<TId = any> = Partial<Omit<MuiAutocompleteProps<any, false, true, true>, 'options'>> & IFieldInputProps<TId, any, string, TId> & IAdditionalFormInputProps

export interface IAutocompleteItem<TId = any> {
    id: TId,
    value: string
}

export interface IAutocompleteProps<TId = any> extends AutocompleteProps<TId> {
    TextFieldProps?: TextFieldProps;
    options?: IAutocompleteItem<TId>[];
}

export interface IAdditionalFormInputProps {
    disabled: boolean;
    hasErrors: boolean;
    errors: string[];
    errorType?: string;
    label?: string;
}


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const InputAutocomplete = <TId = any>({
    FieldInputProps: { state: { value }, actions: { update, blur, focus, touch } },
    label,
    fullWidth,
    TextFieldProps,
    hasErrors,
    errors,
    errorType,
    options,
    ...rest
}: IAutocompleteProps<TId>) => {

    const optionsToUse = useMemo(() => {
        return options ?? [];
    }, [options]);

    const valueToUse = useMemo(() => {
        return optionsToUse?.find(x => x.id === value) ?? null
    }, [optionsToUse, value])

    return (
        <MuiAutocomplete
            {...rest as any}
            options={optionsToUse}
            getOptionLabel={(option: IAutocompleteItem<TId>) => option.value}
            getOptionKey={(option: IAutocompleteItem<TId>) => `${option.value}-${option.id}`}
            fullWidth={fullWidth === undefined ? true : fullWidth}
            value={valueToUse}
            onChange={(_event, newInputValue?: IAutocompleteItem<TId>) => {
                update(newInputValue ? newInputValue?.id : undefined as any, true, true, true);
            }}
            renderInput={(params) => {
                const onFocusCombined = (event: React.FocusEvent<HTMLInputElement>) => {
                    if (params.inputProps.onFocus) {
                        params.inputProps.onFocus(event);
                    }

                    focus();
                };

                const onBlurCombined = (event: React.FocusEvent<HTMLInputElement>) => {
                    if (params.inputProps.onBlur) {
                        params.inputProps.onBlur(event);
                    }

                    blur();
                    touch();
                };

                return (
                    <TextField
                        {...TextFieldProps}
                        {...params}
                        label={TextFieldProps?.label ?? label}
                        InputLabelProps={{
                            ...TextFieldProps?.InputLabelProps,
                            ...params.InputLabelProps
                        }}
                        InputProps={{
                            ...TextFieldProps?.InputProps,
                            ...params.InputProps
                        }}
                        inputProps={{
                            ...TextFieldProps?.inputProps,
                            ...params.inputProps,
                            onBlur: onBlurCombined,
                            onFocus: onFocusCombined
                        }}
                        sx={{...TextFieldProps?.sx}}
                        error={hasErrors}
                    />
                )
            }}
        />
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default InputAutocomplete;
